/**
 * Pure fold of an agent-proposed plan (GraphOp[]) onto the current ReactFlow
 * graph. Always goes through the exact same graphUtils.ts functions manual
 * canvas interactions use (insertNewChild / insertNewChildRaw / deleteNodes)
 * — never a separate bulk-mutation path — so the paths-merge rule,
 * subgraphIndex allocation, and edgeOrder bookkeeping all keep working
 * without reimplementation.
 *
 * All-or-nothing: on any op failure, returns the *original* nodes/edges
 * arrays unchanged (referentially), plus which op failed and why. Callers
 * (LBCanvas.tsx) are expected to call saveCheckpoint() ONCE before applying
 * a whole plan, then setNodes/setEdges with this function's output — never
 * per-op — so one AI turn is one undo step.
 */

import type {
  BoolExprGroup,
  ComparisonExpr,
  ConditionalData,
  RhsValue,
  TeamNotifRecipients,
  TextTemplateChunk,
  VariableRef,
  VariableValue,
} from '@/proto/logiblocks/logiblocks';
import {
  AIAgentType,
  AIChannelType,
  ComparisonOp,
  DayOfWeek,
  LogicalOp,
  RepeatUnit,
  TransferToEscalationOption,
} from '@/proto/logiblocks/logiblocks';
import { VariableSource, VariableType } from '@/proto/common';
import {
  createNode,
  defaultNodeData,
  deleteNodes,
  insertNewChild,
  insertNewChildRaw,
} from '../graphUtils';
import { getLBNodeType } from '../nodes/registry';
import type {
  LBNodeDataWithIndexer,
  ReactFlowEdge,
  ReactFlowNode,
} from '../types';
import {
  isRef,
  REF_PATTERN,
  type AddConditionOp,
  type FieldValue,
  type GraphOpsVersion,
  type GraphOp,
} from './graphOps';
import {
  clearProtoPaths,
  cloneBoundedProtoJson,
  mergeProtoJson,
  resolveProtoReferences,
  type AuthoringSchema,
} from './authoringSchema';

export type ChangedNodeKind = 'added' | 'modified';

/** Snapshot of {nodes, edges} captured immediately after one op in the plan,
 * with stable ids (unlike re-running applyGraphOps on growing op prefixes,
 * which would mint fresh uuids each time via createNode). Used by
 * usePlanReveal to replay a plan step-by-step without recomputing it. */
export interface OpStepResult {
  opIndex: number;
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
  addedNodeIds: string[];
  addedEdgeIds: string[];
  removedNodeIds: string[];
  modifiedNodeIds: string[];
  primaryNodeId: string | undefined;
}

export interface ApplyGraphOpsOptions {
  /** When true, ApplyGraphOpsSuccess.steps carries one OpStepResult per op.
   * Off by default so existing callers/tests are unaffected. */
  recordSteps?: boolean;
  authoringSchema?: AuthoringSchema;
  catalogHash?: string;
  /** Parsed operations are always format 2; response readers require its explicit pin. */
  graphOpsVersion?: GraphOpsVersion;
}

export interface ApplyGraphOpsSuccess {
  kind: 'success';
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
  /** Node ids added or reconfigured by this plan, for the "what changed" UI. */
  changedNodeIds: Map<string, ChangedNodeKind>;
  /** Edge ids created by this plan (every insertNewChild / insertNewChildRaw
   * call mints a fresh id, so this is always "added", never "modified"). */
  changedEdgeIds: Set<string>;
  /** Resolved $ref -> real node id, e.g. so the caller can locate "the trigger". */
  refMap: Map<string, string>;
  /** Present only when options.recordSteps is true. */
  steps?: OpStepResult[];
}

export interface ApplyGraphOpsFailure {
  kind: 'failure';
  failedOpIndex: number;
  reason: string;
}

export type ApplyGraphOpsResult = ApplyGraphOpsSuccess | ApplyGraphOpsFailure;

function numericEnumMembers<T extends number>(
  values: Readonly<Record<string, string | T>>
): Record<string, T> {
  const members: Record<string, T> = {};
  for (const [name, value] of Object.entries(values)) {
    // Numeric protobuf enums also contain reverse number-to-name entries.
    if (typeof value === 'number') members[name] = value;
  }
  return members;
}

// Legacy configure_node enum fields use protobuf names, never the numeric
// reverse mappings. Negotiated authoring uses its own per-node enum schema.
const ENUM_FIELD_MAPS: Record<string, Record<string, number>> = {
  repeat_every: numericEnumMembers(RepeatUnit),
  days_of_week: numericEnumMembers(DayOfWeek),
  agent_type: numericEnumMembers(AIAgentType),
  channel_type: numericEnumMembers(AIChannelType),
  escalation_option: numericEnumMembers(TransferToEscalationOption),
};
const COMPARISON_OPERATORS = numericEnumMembers(ComparisonOp);

// Native template fields when no portable authoring schema accompanies the plan.
const TEMPLATE_CHUNK_FIELDS: Record<string, readonly string[]> = {
  slack_action: ['message_chunks'],
  teams_notification: ['message_chunks'],
  sms_action: ['message_chunks'],
  email_action: ['subject_chunks', 'body_chunks'],
  // Chunk text in these two fields is HTML. The agent's text lands as written.
  resolve_alert_action: ['comment_chunks'],
  add_coc_note_action: ['message_chunks'],
};

function explicitVariableRef(
  nodeId: string,
  value: {
    variableName?: unknown;
    derivedVariableName?: unknown;
    source?: unknown;
  }
): VariableRef {
  if (
    !nodeId ||
    typeof value.variableName !== 'string' ||
    !value.variableName ||
    typeof value.derivedVariableName !== 'string' ||
    (value.source !== VariableSource.SOURCE_NODE_TYPE &&
      value.source !== VariableSource.SOURCE_USER &&
      value.source !== VariableSource.SOURCE_SYSTEM)
  )
    throw new Error(
      'Compiled variable references require explicit identity and source.'
    );
  return {
    node_id: nodeId,
    variable_name: value.variableName,
    derived_variable_name: value.derivedVariableName,
    source: value.source,
  };
}

/**
 * Converts one configure_node field value from the agent's op format to the
 * proto value the node stores.
 *
 * @param resolveRef - Turns a plan-local $ref into a node id.
 */
function convertFieldValue(
  value: FieldValue,
  resolveRef: (ref: string) => string | null
): { kind: 'success'; value: unknown } | { kind: 'failure' } {
  switch (value.kind) {
    case 'string':
      return { kind: 'success', value: value.stringValue ?? '' };
    case 'number':
      return { kind: 'success', value: value.numberValue ?? 0 };
    case 'bool':
      return { kind: 'success', value: value.boolValue ?? false };
    case 'string_list':
      return { kind: 'success', value: value.stringListValue ?? [] };
    case 'number_list':
      return { kind: 'success', value: value.numberListValue ?? [] };
    case 'time_of_day_list':
      return {
        kind: 'success',
        value: (value.timeOfDayListValue ?? []).map((t) => ({
          hour: t.hour,
          minute: t.minute,
        })),
      };
    case 'date_range_list': {
      // Maps to the proto's map<int32, FilterDateRange> -- keyed by each
      // entry's position in the list, since the integer key itself has no
      // meaning beyond uniqueness (same convention the manual UI's own
      // FilterControlFlowCustomization.tsx uses for these maps).
      const entries: Record<
        number,
        { period: string; start: string; end: string }
      > = {};
      (value.dateRangeListValue ?? []).forEach((d, index) => {
        entries[index] = { period: d.period, start: d.start, end: d.end };
      });
      return { kind: 'success', value: entries };
    }
    case 'team_recipient_list':
      // Only used for email_action's to/cc/bcc (repeated TeamNotifRecipients).
      // Empty/absent userIds on an entry means "everyone on that team"
      // (selectAll=true); a non-empty list means just those people.
      return {
        kind: 'success',
        value: (value.teamRecipientListValue ?? []).map(
          (entry): TeamNotifRecipients => ({
            team_id: entry.teamId,
            select_all: !entry.userIds || entry.userIds.length === 0,
            user_ids: entry.userIds ?? [],
            // The agent's op format has no field for additional contacts.
            additional_recipients: [],
          })
        ),
      };
    case 'variable_ref': {
      if (!value.variableRefValue) return { kind: 'failure' };
      const nodeId = resolveRef(value.variableRefValue.nodeRef);
      if (!nodeId) return { kind: 'failure' };
      const ref = explicitVariableRef(nodeId, value.variableRefValue);
      return { kind: 'success', value: ref };
    }
    case 'template_chunks': {
      if (!Array.isArray(value.templateChunksValue)) return { kind: 'failure' };
      const chunks: TextTemplateChunk[] = [];
      for (const chunk of value.templateChunksValue ?? []) {
        if ('text' in chunk) {
          if (chunk.text.includes('{{')) return { kind: 'failure' };
          chunks.push({ text: chunk.text });
          continue;
        }
        const nodeId = resolveRef(chunk.variable.nodeRef);
        if (!nodeId) return { kind: 'failure' };
        chunks.push({
          variable: explicitVariableRef(nodeId, chunk.variable),
        });
      }
      return { kind: 'success', value: chunks };
    }
    case 'enum':
    case 'enum_list':
    case 'protobuf_json':
      // These require field-specific schema handling in configure_node;
      // this context-free converter must never accept them on its own.
      return { kind: 'failure' };
    default:
      return { kind: 'failure' };
  }
}

/**
 * Converts an add_condition right-hand side from the agent's op format to the
 * RhsValue the condition stores.
 *
 * @param resolveRef - Turns a plan-local $ref into a node id.
 */
function convertToRhsValue(
  value: FieldValue,
  resolveRef: (ref: string) => string | null
): RhsValue | undefined {
  if (value.kind === 'variable_ref') {
    if (!value.variableRefValue) return undefined;
    const nodeId = resolveRef(value.variableRefValue.nodeRef);
    if (!nodeId) return undefined;
    return {
      variable: explicitVariableRef(nodeId, value.variableRefValue),
    };
  }
  if (value.kind === 'string_list') {
    return {
      list: {
        items: (value.stringListValue ?? []).map((s): VariableValue => ({
          string_value: s,
          type: VariableType.STRING,
        })),
      },
    };
  }
  if (value.kind === 'number') {
    return {
      literal: {
        number_value: value.numberValue ?? 0,
        type: VariableType.NUMBER,
      },
    };
  }
  if (value.kind === 'bool') {
    return {
      literal: {
        bool_value: value.boolValue ?? false,
        type: VariableType.BOOL,
      },
    };
  }
  if (value.kind !== 'string') return undefined;
  const text = value.stringValue ?? '';
  return { literal: { string_value: text, type: VariableType.STRING } };
}

export function applyGraphOps(
  ops: GraphOp[],
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[],
  options?: ApplyGraphOpsOptions
): ApplyGraphOpsResult {
  if ((options?.graphOpsVersion ?? 2) !== 2)
    return {
      kind: 'failure',
      failedOpIndex: 0,
      reason: 'Unsupported GraphOps version.',
    };
  const recordSteps = options?.recordSteps ?? false;
  const originalNodeIds = new Set(nodes.map((n) => n.id));
  const originalEdgeIds = new Set(edges.map((e) => e.id));
  let curNodes = nodes;
  let curEdges = edges;
  const refMap = new Map<string, string>();
  const changedNodeIds = new Map<string, ChangedNodeKind>();
  const steps: OpStepResult[] | undefined = recordSteps ? [] : undefined;
  let currentStepModifiedNodeIds = new Set<string>();

  const fail = (index: number, reason: string): ApplyGraphOpsFailure => ({
    kind: 'failure',
    failedOpIndex: index,
    reason,
  });
  if (
    options?.authoringSchema &&
    options.catalogHash !== options.authoringSchema.catalog_hash
  )
    return fail(
      0,
      'Copilot candidate catalog does not match its authoring schema.'
    );

  const pushStep = (
    opIndex: number,
    primaryNodeId: string | undefined,
    beforeNodeIds: Set<string> | undefined,
    beforeEdgeIds: Set<string> | undefined
  ) => {
    if (!steps || !beforeNodeIds || !beforeEdgeIds) return;
    const addedNodeIds = curNodes
      .map((n) => n.id)
      .filter((id) => !beforeNodeIds.has(id));
    const addedEdgeIds = curEdges
      .map((e) => e.id)
      .filter((id) => !beforeEdgeIds.has(id));
    const removedNodeIds = [...beforeNodeIds].filter(
      (id) => !curNodes.some((n) => n.id === id)
    );
    steps.push({
      opIndex,
      nodes: curNodes,
      edges: curEdges,
      addedNodeIds,
      addedEdgeIds,
      removedNodeIds,
      modifiedNodeIds: [...currentStepModifiedNodeIds],
      primaryNodeId,
    });
  };

  const resolveRef = (ref: string): string | null => {
    if (isRef(ref)) return refMap.get(ref) ?? null;
    return ref;
  };

  const markChanged = (nodeId: string, kind: ChangedNodeKind) => {
    if (!changedNodeIds.has(nodeId)) {
      changedNodeIds.set(nodeId, kind);
    }
    if (kind === 'modified') currentStepModifiedNodeIds.add(nodeId);
  };

  const buildComparisonExpr = (
    condition: Omit<AddConditionOp, 'op' | 'conditional'>
  ): ComparisonExpr | null => {
    const lhsNodeId = resolveRef(condition.lhs.nodeRef);
    if (!lhsNodeId) return null;
    const operatorValue = COMPARISON_OPERATORS[condition.operator];
    if (operatorValue === undefined) return null;
    const rhs = condition.rhs
      ? convertToRhsValue(condition.rhs, resolveRef)
      : undefined;
    const rhs2 = condition.rhs2
      ? convertToRhsValue(condition.rhs2, resolveRef)
      : undefined;
    if ((condition.rhs && !rhs) || (condition.rhs2 && !rhs2)) return null;
    return {
      variable: explicitVariableRef(lhsNodeId, condition.lhs),
      operator: operatorValue,
      rhs,
      rhs2,
    };
  };

  try {
    for (let i = 0; i < ops.length; i++) {
      currentStepModifiedNodeIds = new Set<string>();
      const op = ops[i];
      const beforeNodeIds = recordSteps
        ? new Set(curNodes.map((n) => n.id))
        : undefined;
      const beforeEdgeIds = recordSteps
        ? new Set(curEdges.map((e) => e.id))
        : undefined;

      if (op.op === 'add_node') {
        if (
          options?.authoringSchema &&
          !options.authoringSchema.nodes[op.nodeType]?.eligible
        )
          return fail(
            i,
            `The negotiated catalog does not permit creating ${op.nodeType}.`
          );
        if (!REF_PATTERN.test(op.ref)) {
          return fail(i, `ref ${op.ref} is not a valid $ref`);
        }

        let childNode: ReactFlowNode;
        if (op.parent === '') {
          if (curNodes.length !== 0) {
            return fail(
              i,
              'parent may only be empty when the graph has no nodes at ' +
                'this point in the plan (either a genuinely empty graph, ' +
                'or every prior node was just deleted by an earlier op in ' +
                'this same plan) -- there can only be one root/trigger node'
            );
          }
          const data = defaultNodeData(op.nodeType);
          childNode = createNode(data);
          curNodes = [childNode];
        } else {
          const parentId = resolveRef(op.parent);
          if (!parentId) {
            return fail(
              i,
              `parent ${op.parent} does not resolve to a known node`
            );
          }
          const parentNode = curNodes.find((n) => n.id === parentId);
          if (!parentNode) {
            return fail(i, `parent node ${parentId} not found in graph`);
          }
          if (
            op.nodeType === 'conditional' &&
            getLBNodeType(parentNode.data as never) === 'paths'
          ) {
            return fail(
              i,
              'branches under a paths node must use add_branch, not add_node(conditional)'
            );
          }
          // autoPopulatePathsChildren: false -- plans always add every
          // branch explicitly via its own add_branch op; the manual-
          // editing default of auto-creating 2 branches here would double
          // count (2 defaults + N explicit add_branch calls).
          const result = insertNewChild(
            parentNode,
            op.nodeType,
            curNodes,
            curEdges,
            {
              autoPopulatePathsChildren: false,
            }
          );
          if (!result) {
            return fail(
              i,
              `insertNewChild failed for parent ${parentId}, type ${op.nodeType}`
            );
          }
          curNodes = result.nodes;
          curEdges = result.edges;
          childNode = result.childNode;
        }

        refMap.set(op.ref, childNode.id);
        // insertNewChild returns the EXISTING paths node when merging a
        // second paths child under the same parent (see graphUtils.ts) —
        // that's a modification, not a fresh addition.
        markChanged(
          childNode.id,
          originalNodeIds.has(childNode.id) ? 'modified' : 'added'
        );

        if (op.displayName) {
          const displayName = op.displayName;
          curNodes = curNodes.map((n) =>
            n.id === childNode.id
              ? {
                  ...n,
                  data: Object.freeze({
                    ...n.data,
                    custom_display_name: displayName,
                  }) as LBNodeDataWithIndexer,
                }
              : n
          );
        }
        pushStep(i, childNode.id, beforeNodeIds, beforeEdgeIds);
        continue;
      }

      if (op.op === 'wrap_with_paths') {
        if (isRef(op.parent)) {
          return fail(i, 'wrap_with_paths.parent must be a saved id');
        }
        const parentNode = curNodes.find((node) => node.id === op.parent);
        if (!parentNode) {
          return fail(i, `parent node ${op.parent} not found in graph`);
        }
        const wrapped = insertNewChild(
          parentNode,
          'paths',
          curNodes,
          curEdges,
          { autoPopulatePathsChildren: true }
        );
        if (!wrapped || originalNodeIds.has(wrapped.childNode.id)) {
          return fail(
            i,
            `could not create a fresh Paths split under ${op.parent}`
          );
        }
        curNodes = wrapped.nodes;
        curEdges = wrapped.edges;
        const pathsNode = wrapped.childNode;
        const branchIds = curEdges
          .filter((edge) => edge.source === pathsNode.id)
          .sort(
            (left, right) =>
              (left.data?.edgeOrder ?? 0) - (right.data?.edgeOrder ?? 0)
          )
          .map((edge) => edge.target);
        while (branchIds.length < op.branchRefs.length) {
          const branch = insertNewChildRaw(
            pathsNode,
            'conditional',
            curNodes,
            curEdges
          );
          if (!branch) {
            return fail(i, `could not add a branch under ${pathsNode.id}`);
          }
          curNodes = branch.nodes;
          curEdges = branch.edges;
          branchIds.push(branch.childNode.id);
        }
        if (branchIds.length !== op.branchRefs.length) {
          return fail(i, 'Paths split produced an unexpected branch count');
        }
        refMap.set(op.pathsRef, pathsNode.id);
        markChanged(pathsNode.id, 'added');
        op.branchRefs.forEach((ref, branchIndex) => {
          refMap.set(ref, branchIds[branchIndex]);
          markChanged(branchIds[branchIndex], 'added');
        });
        pushStep(i, pathsNode.id, beforeNodeIds, beforeEdgeIds);
        continue;
      }

      if (op.op === 'configure_node') {
        const nodeId = resolveRef(op.node);
        if (!nodeId) {
          return fail(i, `node ${op.node} does not resolve to a known node`);
        }
        const node = curNodes.find((n) => n.id === nodeId);
        if (!node) {
          return fail(i, `node ${nodeId} not found in graph`);
        }
        const nodeType = getLBNodeType(node.data as never);
        if (!nodeType) {
          return fail(i, `could not determine node type for ${nodeId}`);
        }
        const currentVariant =
          ((node.data as LBNodeDataWithIndexer)[nodeType] as
            Record<string, unknown> | undefined) ?? {};
        const newVariant: Record<string, unknown> = { ...currentVariant };
        const nodeSchema = options?.authoringSchema?.nodes[nodeType];
        if (options?.authoringSchema && !nodeSchema)
          return fail(i, `No negotiated schema for ${nodeType}.`);

        const selectedOneofs = new Set<string>();
        for (const f of op.fields) {
          const schema = nodeSchema?.fields[f.name];
          if (
            nodeSchema &&
            (!schema || schema.classification !== 'configurable')
          )
            return fail(
              i,
              `Field ${f.name} is not configurable in the negotiated schema.`
            );
          if (schema?.oneof) {
            if (selectedOneofs.has(schema.oneof))
              return fail(i, 'Conflicting protobuf oneof members.');
            selectedOneofs.add(schema.oneof);
            for (const [name, sibling] of Object.entries(nodeSchema!.fields))
              if (sibling.oneof === schema.oneof && name !== f.name)
                delete newVariant[name];
          }
          if (f.value.kind === 'protobuf_json') {
            if (!nodeSchema || !schema)
              return fail(
                i,
                'Nested protobuf authoring requires a negotiated schema.'
              );
            const patch = resolveProtoReferences(
              cloneBoundedProtoJson(f.value.protobufJsonValue),
              schema,
              nodeSchema.$defs,
              (raw) => {
                const id =
                  typeof raw.node_id === 'string'
                    ? resolveRef(raw.node_id)
                    : null;
                if (!id || typeof raw.variable_name !== 'string')
                  throw new Error(
                    'Nested variable reference does not resolve.'
                  );
                return {
                  ...explicitVariableRef(id, {
                    variableName: raw.variable_name,
                    derivedVariableName: raw.derived_variable_name,
                    source: raw.source,
                  }),
                };
              }
            );
            newVariant[f.name] = clearProtoPaths(
              mergeProtoJson(
                newVariant[f.name],
                patch,
                schema,
                nodeSchema.$defs
              ),
              f.value.clearPaths ?? [],
              schema,
              nodeSchema.$defs
            );
            continue;
          }
          if (f.value.kind === 'enum' || f.value.kind === 'enum_list') {
            const enumMap = nodeSchema
              ? (schema?.enum_values ?? schema?.items?.enum_values)
              : ENUM_FIELD_MAPS[f.name];
            if (!enumMap) {
              return fail(i, `no enum mapping known for field ${f.name}`);
            }
            if (f.value.kind === 'enum') {
              const enumValue = enumMap[f.value.enumValue ?? ''];
              if (enumValue === undefined) {
                return fail(
                  i,
                  `unknown enum value ${f.value.enumValue} for field ${f.name}`
                );
              }
              newVariant[f.name] = enumValue;
            } else {
              const values = (f.value.enumListValue ?? []).map(
                (name) => enumMap[name]
              );
              if (values.some((v) => v === undefined)) {
                return fail(
                  i,
                  `unknown enum value in field ${f.name}: ${JSON.stringify(f.value.enumListValue)}`
                );
              }
              newVariant[f.name] = values;
            }
            continue;
          }
          const converted = convertFieldValue(f.value, resolveRef);
          if (converted.kind === 'failure') {
            return fail(
              i,
              `could not convert field ${f.name} on node ${nodeId}`
            );
          }
          const isTemplateChunkField = nodeSchema
            ? nodeSchema.template_mappings.some(
                (mapping) => mapping.chunk_path === f.name
              )
            : TEMPLATE_CHUNK_FIELDS[nodeType]?.includes(f.name) === true;
          if (f.value.kind === 'template_chunks') {
            if (!isTemplateChunkField || !Array.isArray(converted.value)) {
              return fail(
                i,
                `field ${f.name} does not support template chunks`
              );
            }
            newVariant[f.name] = converted.value;
            continue;
          }
          if (isTemplateChunkField)
            return fail(i, `field ${f.name} requires compiled template chunks`);
          newVariant[f.name] = converted.value;
        }

        curNodes = curNodes.map((n) =>
          n.id === nodeId
            ? {
                ...n,
                data: Object.freeze({
                  ...n.data,
                  [nodeType]: newVariant,
                }) as LBNodeDataWithIndexer,
              }
            : n
        );
        markChanged(nodeId, 'modified');
        pushStep(i, nodeId, beforeNodeIds, beforeEdgeIds);
        continue;
      }

      if (op.op === 'replace_node_user_variables') {
        if (isRef(op.node)) {
          return fail(i, 'replace_node_user_variables.node must be a saved id');
        }
        const nodeId = resolveRef(op.node);
        if (!nodeId) {
          return fail(i, `node ${op.node} does not resolve to a known node`);
        }
        const node = curNodes.find((candidate) => candidate.id === nodeId);
        if (!node || getLBNodeType(node.data as never) !== 'ai_call_action') {
          return fail(i, `${nodeId} is not an AI call node`);
        }
        const currentData = node.data as LBNodeDataWithIndexer;
        const currentAICall = currentData.ai_call_action;
        if (!currentAICall || currentAICall.agent_id !== op.agentId) {
          return fail(
            i,
            `AI agent ${op.agentId} is not configured on node ${nodeId}`
          );
        }
        curNodes = curNodes.map((candidate) =>
          candidate.id === nodeId
            ? {
                ...candidate,
                data: Object.freeze({
                  ...candidate.data,
                  user_variables: op.userVariables,
                  ai_call_action: {
                    ...currentAICall,
                    collect_shipment_id: op.collectShipmentId,
                  },
                }) as LBNodeDataWithIndexer,
              }
            : candidate
        );
        markChanged(nodeId, 'modified');
        pushStep(i, nodeId, beforeNodeIds, beforeEdgeIds);
        continue;
      }

      if (op.op === 'replace_node_conditions') {
        if (isRef(op.node)) {
          return fail(i, 'replace_node_conditions.node must be a saved id');
        }
        const nodeId = resolveRef(op.node);
        const node = curNodes.find((candidate) => candidate.id === nodeId);
        if (!node || getLBNodeType(node.data as never) !== 'conditional') {
          return fail(i, `${op.node} is not a conditional node`);
        }
        const expressions: BoolExprGroup['expressions'] = [];
        for (const condition of op.conditions) {
          const comparisonExpr = buildComparisonExpr(condition);
          if (!comparisonExpr) {
            return fail(i, `could not replace conditions on node ${node.id}`);
          }
          expressions.push({ comparison_expr: comparisonExpr });
        }
        const operators = expressions.slice(1).map(() => LogicalOp.AND);
        curNodes = curNodes.map((candidate) =>
          candidate.id === node.id
            ? {
                ...candidate,
                data: Object.freeze({
                  ...candidate.data,
                  conditional: { root: { expressions, operators } },
                }) as LBNodeDataWithIndexer,
              }
            : candidate
        );
        markChanged(node.id, 'modified');
        pushStep(i, node.id, beforeNodeIds, beforeEdgeIds);
        continue;
      }

      if (op.op === 'add_branch') {
        if (!REF_PATTERN.test(op.ref)) {
          return fail(i, `ref ${op.ref} is not a valid $ref`);
        }
        const pathsId = resolveRef(op.pathsNode);
        if (!pathsId) {
          return fail(i, `paths_node ${op.pathsNode} does not resolve`);
        }
        const pathsNodeObj = curNodes.find((n) => n.id === pathsId);
        if (!pathsNodeObj) {
          return fail(i, `paths node ${pathsId} not found in graph`);
        }
        if (getLBNodeType(pathsNodeObj.data as never) !== 'paths') {
          return fail(i, `${pathsId} is not a paths node`);
        }
        const result = insertNewChildRaw(
          pathsNodeObj,
          'conditional',
          curNodes,
          curEdges
        );
        if (!result) {
          return fail(i, `insertNewChildRaw failed for paths node ${pathsId}`);
        }
        curNodes = result.nodes;
        curEdges = result.edges;
        refMap.set(op.ref, result.childNode.id);
        markChanged(result.childNode.id, 'added');

        if (op.label) {
          const label = op.label;
          const branchId = result.childNode.id;
          curNodes = curNodes.map((n) =>
            n.id === branchId
              ? {
                  ...n,
                  data: Object.freeze({
                    ...n.data,
                    custom_display_name: label,
                  }) as LBNodeDataWithIndexer,
                }
              : n
          );
        }
        pushStep(i, result.childNode.id, beforeNodeIds, beforeEdgeIds);
        continue;
      }

      if (op.op === 'add_condition') {
        const condId = resolveRef(op.conditional);
        if (!condId) {
          return fail(i, `conditional ${op.conditional} does not resolve`);
        }
        const condNode = curNodes.find((n) => n.id === condId);
        if (!condNode) {
          return fail(i, `conditional node ${condId} not found in graph`);
        }
        if (getLBNodeType(condNode.data as never) !== 'conditional') {
          return fail(i, `${condId} is not a conditional node`);
        }
        const { op: _op, conditional: _conditional, ...condition } = op;
        const comparisonExpr = buildComparisonExpr(condition);
        if (!comparisonExpr) {
          return fail(i, `could not build condition on node ${condId}`);
        }
        const currentData = condNode.data as LBNodeDataWithIndexer;
        const currentConditional = (currentData.conditional as
          ConditionalData | undefined) ?? {
          root: undefined,
        };
        const currentRoot: BoolExprGroup = currentConditional.root ?? {
          expressions: [],
          operators: [],
        };
        const newExpressions = [
          ...currentRoot.expressions,
          { comparison_expr: comparisonExpr },
        ];
        const newOperators =
          currentRoot.expressions.length > 0
            ? [...currentRoot.operators, LogicalOp.AND]
            : currentRoot.operators;
        const newConditional: ConditionalData = {
          root: { expressions: newExpressions, operators: newOperators },
        };

        curNodes = curNodes.map((n) =>
          n.id === condId
            ? {
                ...n,
                data: Object.freeze({
                  ...n.data,
                  conditional: newConditional,
                }) as LBNodeDataWithIndexer,
              }
            : n
        );
        markChanged(condId, 'modified');
        pushStep(i, condId, beforeNodeIds, beforeEdgeIds);
        continue;
      }

      if (op.op === 'delete_node') {
        if (isRef(op.node)) {
          return fail(
            i,
            'delete_node.node must be an existing id, never a $ref'
          );
        }
        if (!curNodes.some((n) => n.id === op.node)) {
          return fail(i, `node ${op.node} not found in graph`);
        }
        const result = deleteNodes([op.node], curNodes, curEdges);
        curNodes = result.nodes;
        curEdges = result.edges;
        changedNodeIds.delete(op.node);
        pushStep(i, op.node, beforeNodeIds, beforeEdgeIds);
        continue;
      }

      if (op.op === 'rename_node') {
        const nodeId = resolveRef(op.node);
        if (!nodeId) {
          return fail(i, `node ${op.node} does not resolve to a known node`);
        }
        if (!curNodes.some((n) => n.id === nodeId)) {
          return fail(i, `node ${nodeId} not found in graph`);
        }
        curNodes = curNodes.map((n) =>
          n.id === nodeId
            ? {
                ...n,
                data: Object.freeze({
                  ...n.data,
                  custom_display_name: op.displayName,
                }) as LBNodeDataWithIndexer,
              }
            : n
        );
        markChanged(nodeId, 'modified');
        pushStep(i, nodeId, beforeNodeIds, beforeEdgeIds);
        continue;
      }

      return fail(i, `unrecognized op`);
    }
  } catch (err) {
    return fail(-1, err instanceof Error ? err.message : String(err));
  }

  const changedEdgeIds = new Set(
    curEdges.filter((e) => !originalEdgeIds.has(e.id)).map((e) => e.id)
  );

  return {
    kind: 'success',
    nodes: curNodes,
    edges: curEdges,
    changedNodeIds,
    changedEdgeIds,
    refMap,
    ...(steps ? { steps } : {}),
  };
}
